import React, { useState, useEffect, useRef } from "react";
import { Popover, PopoverBody, Input, Button } from "reactstrap";
import { useIntl, FormattedMessage } from "react-intl";
import { compareByName } from "../../Utilities/sortHelper";

const StudentPicker = ({ target, isOpen, toggle, students, onAdd }) => {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus({ preventScroll: true });
    } else {
      setQuery("");
      setPicked(new Set());
    }
  }, [isOpen]);

  const lq = query.toLowerCase();
  const filtered = students
    .filter((s) => !lq || s.name.toLowerCase().includes(lq))
    .sort(compareByName);

  const filteredIds = filtered.map((s) => s.id);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => picked.has(id));
  const anyFilteredSelected = filteredIds.some((id) => picked.has(id));

  const togglePick = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleAdd = () => {
    const selectedStudents = students.filter((s) => picked.has(s.id));
    onAdd(selectedStudents);
    toggle();
  };

  return (
    <Popover
      placement="bottom-end"
      isOpen={isOpen}
      target={target}
      toggle={toggle}
      hideArrow={false}
      trigger="legacy"
      popperClassName="aplmod-picker"
    >
      <PopoverBody>
        <div className="aplmod-picker__search">
          <Input
            innerRef={searchInputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={intl.formatMessage({ id: "SearchStudent" })}
          />
        </div>
        <div className="aplmod-picker__list">
          {filtered.length === 0 ? (
            <div className="aplmod-picker__empty">
              <FormattedMessage id="NoMatches" />
            </div>
          ) : (
            <>
              <label className="aplmod-picker__row aplmod-picker__row--checkable aplmod-picker__row--all">
                <Input
                  type="checkbox"
                  checked={allFilteredSelected}
                  innerRef={(el) => {
                    if (el)
                      el.indeterminate =
                        anyFilteredSelected && !allFilteredSelected;
                  }}
                  onChange={toggleAllFiltered}
                  style={{ marginTop: 0, marginLeft: 0, position: "relative" }}
                />
                <span style={{ fontWeight: 600 }}>
                  <FormattedMessage
                    id={
                      allFilteredSelected ? "DeselectAll" : "SelectAllStudents"
                    }
                  />
                  <span
                    style={{ color: "#6c757d", fontWeight: 400, marginLeft: 4 }}
                  >
                    ({filtered.length})
                  </span>
                </span>
              </label>
              {filtered.map((s) => (
                <label
                  key={s.id}
                  className={`aplmod-picker__row aplmod-picker__row--checkable${
                    picked.has(s.id) ? " aplmod-picker__row--selected" : ""
                  }`}
                >
                  <Input
                    type="checkbox"
                    checked={picked.has(s.id)}
                    onChange={() => togglePick(s.id)}
                    style={{
                      marginTop: 0,
                      marginLeft: 0,
                      position: "relative",
                    }}
                  />
                  <span>{s.name}</span>
                </label>
              ))}
            </>
          )}
        </div>
        <div className="aplmod-picker__footer d-flex gap-2">
          <Button
            outline
            color="secondary"
            onClick={toggle}
            className="flex-fill"
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            outline
            color="primary"
            disabled={picked.size === 0}
            onClick={handleAdd}
            className="flex-fill"
          >
            <FormattedMessage
              id={picked.size > 0 ? "AddSelectedWithCount" : "AddSelected"}
              values={{ count: picked.size }}
            />
          </Button>
        </div>
      </PopoverBody>
    </Popover>
  );
};

export default StudentPicker;
